import type { PlainDate } from "@sa-nu/shared-utils/date";
import { isNationalHoliday } from "@sa-nu/shared-utils/date";
import clsx from "clsx";

/** 日付セルの付加情報（サイクル区切り・翌7日など） */
export type DateCellDateType = {
  isCycleEnd: boolean;
  next7: "none" | "in" | "end";
};

export const NONE_DATE_TYPE = {
  isCycleEnd: false,
  next7: "none",
} as const satisfies DateCellDateType;

type TextKind = "default" | "saturday" | "sunday";

const WEEKDAY_LABELS = ["月", "火", "水", "木", "金", "土", "日"] as const;

function getTextKind(date: PlainDate): TextKind {
  if (date.dayOfWeek === 6) return "saturday";
  if (date.dayOfWeek === 7 || isNationalHoliday(date)) return "sunday";
  return "default";
}

/** ボタン右ボーダーのスタイル（色・dashed）を決定 */
function determineRightBorderStyle(
  dateType: DateCellDateType,
  isInternal: boolean,
): string {
  if (isInternal) return "border-r-gray-200";
  if (dateType.isCycleEnd) return "border-r-skogecha-60";
  if (dateType.next7 === "end") return "border-dashed border-r-skogecha-60";
  return "border-r-gray-200";
}

/** 内側ボックスのテキスト・ボーダー色を決定 */
function determineInnerBoxTextStyle(
  isCICOHighlight: boolean,
  textKind: TextKind,
): string {
  let textColor = "text-skogecha";
  const CICOHighlight = isCICOHighlight ? " border-sorange" : "";

  if (textKind === "saturday") textColor = "text-oldsblue2";
  if (textKind === "sunday") textColor = "text-sorange";
  return `${textColor}${CICOHighlight}`;
}

/** 上部オレンジ線を表示するか */
function shouldShowTopBar(
  dateType: DateCellDateType,
  isInternal: boolean,
): boolean {
  if (isInternal) return false;
  return dateType.next7 !== "none";
}

export function DateCell({
  date,
  dateType,
  isCICOHighlight,
  isInternal,
  onClick,
}: {
  /** 表示する日付 */
  date: PlainDate;
  /** 付加情報: サイクル終了・翌7日・翌7日終了 */
  dateType: DateCellDateType;
  /** CI〜CO の宿泊日に含まれるか */
  isCICOHighlight: boolean;
  /** 内部向け表示か（付加情報の一部スタイルを出さない） */
  isInternal: boolean;
  onClick?: () => void;
}) {
  const textKind = getTextKind(date);
  const dateLabel = `${date.month}/${date.day}`;
  const weekdayLabel = WEEKDAY_LABELS[date.dayOfWeek - 1] ?? "?";

  // styles
  const rightBorderStyle = determineRightBorderStyle(dateType, isInternal);
  const innerBoxTextStyle = determineInnerBoxTextStyle(
    isCICOHighlight,
    textKind,
  );
  const showTopBar = shouldShowTopBar(dateType, isInternal);

  return (
    <button
      data-analytics-id="reservation_calendar_selected_date"
      type="button"
      className={clsx(
        "relative flex h-[46px] w-full flex-1 grow items-center justify-center border-y-0 border-r border-l-0 px-px hover:opacity-60",
        rightBorderStyle,
        isCICOHighlight && "bg-[#FFCBBE]",
        !isCICOHighlight && "bg-swhite",
      )}
      onClick={onClick}
    >
      <div
        className={clsx(
          "border-sbeige bg-swhite flex h-[42px] w-[calc(100%-1px)] flex-col items-center justify-center gap-[4px] rounded-[4px] border px-[4px]",
          innerBoxTextStyle,
        )}
      >
        {showTopBar && (
          <div className="border-t-sorange absolute top-0 left-0 w-full border-t-2" />
        )}
        <div className="font-notojp text-[12px] leading-[1.2] font-bold">
          {dateLabel}
        </div>
        <div className="font-notojp text-[10px] leading-[1.2]">
          {weekdayLabel}
        </div>
      </div>
    </button>
  );
}
